import React, { useEffect, useMemo, useState } from "react";

import { getApiUrl } from "@/config";
import {
  BrandSettings,
  DEFAULT_BRAND_SETTINGS,
  getBrand,
} from "@/service/Brands";
import EventSystem from "@/service/EventSystem";
import InteractionService, { InteractionApi } from "@/service/Interaction";
import LocalStorageService from "@/service/LocalStorage";
import {
  ApplicationPlatform,
  HansIdResponse,
  Interaction,
  InteractionResult,
  Participant,
  RegistrationStatus,
} from "@/service/Model";
import { getBrandIcons } from "@/styles/Icons";
import { log } from "@/utils/logger";

export type IGlobalResponse = {
  interaction: Interaction;
  interactionResults?: InteractionResult;
};

export type LocalSettings = {
  questionOutroOptIn?: boolean;
};

export type GlobalContextProps = {
  brandSettings: BrandSettings;
  icons: IIcons;
  query: IQuery;
  preview: boolean;
  hyperAvailable: boolean;
  isNewsappStandalone: boolean;
  registrationStatus: RegistrationStatus;
  response?: IGlobalResponse;
  error?: Error;
  tealiumConfig?: ITealium;
  hansId?: string;
  handleRegistration: (
    payload: Record<string, string | boolean>
  ) => Promise<any>;
  setQuestionOutroOptIn: (optIn: boolean) => void;
  questionOutroOptIn: boolean;
};

type GlobalProviderProps = {
  value: {
    query?: IQuery;
    mockResponse?: IGlobalResponse;
  };
  children?: React.ReactNode;
};

const GlobalContext = React.createContext<GlobalContextProps>({
  questionOutroOptIn: false,
  preview: false,
  hyperAvailable: false,
  isNewsappStandalone: false,
  registrationStatus: RegistrationStatus.None,
  query: {},
  brandSettings: DEFAULT_BRAND_SETTINGS,
  handleRegistration: () => new Promise<any>(() => null),
  setQuestionOutroOptIn: () => null,
  icons: {
    Checkmark: "",
    Clock: "",
    Close: "",
    CloseLite: "",
    Facebook: "",
    Fail: "",
    PageLeft: "",
    PageRight: "",
    Picker: "",
    Slider: "",
    Success: "",
    ThumbsDown: "",
    ThumbsUp: "",
    X: "",
    Warning: "",
    WhatsApp: "",
    BubbleCheckmark: "",
  },
});

export const GlobalProvider: React.FC<GlobalProviderProps> = (
  props: GlobalProviderProps
): JSX.Element => {
  const {
    value: { query = {}, mockResponse },
  } = props;

  const isNewsappStandalone = query.platform === ApplicationPlatform.Newsapp;
  const preview = query.preview === true;

  const initialBrandSettings = mockResponse
    ? getBrand(
        mockResponse.interaction.organizationId,
        mockResponse.interaction.category
      )
    : DEFAULT_BRAND_SETTINGS;

  if (query.locale) {
    initialBrandSettings.locale = query.locale.toLocaleLowerCase();
  }

  const initialIcons = getBrandIcons(initialBrandSettings.theme);

  const [hyperAvailable, setHyperAvailable] = useState<boolean>(false);
  const [tealiumConfig, setTealiumConfig] = useState<ITealium | undefined>(
    undefined
  );
  const [hansId, setHansId] = useState<string | undefined>(undefined);
  const [registrationStatus, setRegistrationStatus] =
    useState<RegistrationStatus>(RegistrationStatus.None);
  const [error, setError] = useState<Error | undefined>(undefined);

  const [response, setResponse] = useState<IGlobalResponse | undefined>(
    mockResponse
  );
  const [icons, setIcons] = useState<IIcons>(initialIcons);
  const [brandSettings, setBrandSettings] =
    useState<BrandSettings>(initialBrandSettings);
  const [questionOutroOptIn, setQuestionOutroOptIn] = useState<boolean>(false);

  useEffect(() => {
    // Returns immediately if the response was initialized with a mocked response (jest tests) or interaction id is missing
    if (response || !query.id) {
      return;
    }
    initEventListener();
    init(query.id, preview);
  }, []);

  useEffect(() => {
    if (response) {
      // Sync states with local storage
      LocalStorageService.setLocalSettings(response.interaction.id, {
        questionOutroOptIn,
      });
    }
  }, [questionOutroOptIn]);

  const initEventListener = (): void => {
    EventSystem.subscribe("hyper:ping", () => {
      setHyperAvailable(true);
    });

    // Listener for tealium_id via LA-Hyper
    EventSystem.subscribe(
      "tealium:received",
      (tealiumResponse: ITealium | undefined): void => {
        setTealiumConfig(tealiumResponse);
      }
    );

    EventSystem.subscribe("hansId:received", (data: HansIdResponse) => {
      // hansId = 'rejected': The user has not consented to the use of tracking IDs.
      if (data.consent) {
        // Always cast hansId as string (backwards compatible with UUIDs)
        setHansId(String(data.hansId));
      }
    });
  };
  const init = (interactionId: string, preview: boolean): void => {
    api
      .getInteractionAndResults(interactionId, preview)
      .then(([interactionResponse, resultResponse]) => {
        const interaction = interactionResponse.data!;
        const interactionResults = resultResponse.data;
        setupTheme(interaction.organizationId, interaction.category);
        setResponse({
          interaction,
          interactionResults,
        });
        initLocalSettings(interaction.id);
      })
      .catch((error: Error) => setError(error));
  };

  const setupTheme = (organizationId: string, category: string) => {
    // The theme parameter e.g. "theme=bild-books.fitbook" overrides the configured theme
    if (query.theme) {
      [organizationId, category] = query.theme.split(".");
    }
    const brandSettings = getBrand(organizationId, category);
    if (query.locale) {
      brandSettings.locale = query.locale.toLocaleLowerCase();
    }
    setBrandSettings(brandSettings);
    setIcons(getBrandIcons(brandSettings.theme));
  };

  const initLocalSettings = (interactionId: string) => {
    const localSettings = LocalStorageService.getLocalSettings(interactionId);
    setQuestionOutroOptIn(!!localSettings?.questionOutroOptIn);
  };

  const handleRegistration = async (
    payload: Record<string, string | boolean>
  ): Promise<RegistrationStatus | undefined> => {
    if (!response || !payload) {
      return;
    }

    const { interaction } = response;
    const participant = payload as Participant;

    if (hansId) {
      participant.hansId = hansId;
    }

    if (interaction.outro?.campaignId) {
      participant.campaignId = interaction.outro.campaignId;
    }

    setRegistrationStatus(RegistrationStatus.Pending);

    try {
      const response = await api.postParticipation({
        ...participant,
        interactionId: interaction.id,
        interactionType: interaction.type,
      });
      if (response.ok) {
        setRegistrationStatus(RegistrationStatus.Success);
        return RegistrationStatus.Success;
      } else {
        setRegistrationStatus(RegistrationStatus.Error);
        log.error(
          `Error while submit registration form: ${response.status}: ${response.text}`
        );
      }
    } catch (e) {
      log.error("Error while submit registration form:" + e);
    }
  };

  const api = useMemo<InteractionApi>(
    () => new InteractionService(getApiUrl(preview)),
    [preview]
  );

  const value = useMemo<GlobalContextProps>(
    () => ({
      handleRegistration,
      setQuestionOutroOptIn,
      questionOutroOptIn,
      hyperAvailable,
      isNewsappStandalone,
      preview,
      brandSettings,
      icons,
      query,
      registrationStatus,
      response,
      error,
      tealiumConfig,
      hansId,
    }),
    [
      hyperAvailable,
      isNewsappStandalone,
      preview,
      brandSettings,
      icons,
      query,
      registrationStatus,
      response,
      error,
      tealiumConfig,
      hansId,
      questionOutroOptIn,
    ]
  );

  return (
    <GlobalContext.Provider value={value}>
      {props.children}
    </GlobalContext.Provider>
  );
};

export const GlobalConsumer = GlobalContext.Consumer;
export default GlobalContext;
